import { useEffect, useRef, useState, type ChangeEvent, type RefObject } from 'react'
import type { LocalDataContinuitySnapshot } from '../../data/continuity'
import type { GoogleKeepImportController } from '../../imports/googleKeep/client'
import { GoogleKeepImportPanel } from '../../components/GoogleKeepImport'
import { NoteExportsPanel, NoteExportSettingsSummary } from '../../components/NoteExport'
import { Icon } from '../../components/Icon'
import type { NoteExportController } from '../../exports/client'

type ExportKind = 'markdown' | 'json' | 'recovery'

/** The notes download, in words: idle, working, done or failed (App.tsx exportNotes). */
export interface NotesExportStatus { phase: 'idle' | 'working' | 'done' | 'failed'; title: string; detail: string }

interface DataStorageSettingsProps {
  continuity: LocalDataContinuitySnapshot | null
  storagePersistent: boolean | null
  recoveryExportBusy: boolean
  recoveryImportBusy: boolean
  recoveryImportEnabled: boolean
  recoveryInputRef: RefObject<HTMLInputElement | null>
  onExport(kind: ExportKind): void
  onImportRecovery(event: ChangeEvent<HTMLInputElement>): void
  googleKeepController: GoogleKeepImportController
  googleKeepEnabled: boolean
  googleKeepOpen: boolean
  onGoogleKeepOpenChange(open: boolean): void
  noteExportController: NoteExportController
  notesExport: NotesExportStatus
  /** Only without an account: erases what this device holds. */
  onDeleteDeviceData?: () => void
}

function pendingSummary(continuity: LocalDataContinuitySnapshot): string {
  const parts = [
    continuity.notes.unsynchronized ? `${continuity.notes.unsynchronized} note${continuity.notes.unsynchronized === 1 ? '' : 's'}` : '',
    continuity.activeDraft.containsLocalChanges ? 'an active draft' : '',
    continuity.outbox.total ? `${continuity.outbox.total} queued change${continuity.outbox.total === 1 ? '' : 's'}` : '',
    continuity.assets.unsynchronized ? `${continuity.assets.unsynchronized} image job${continuity.assets.unsynchronized === 1 ? '' : 's'}` : '',
  ].filter(Boolean)
  return parts.length > 0 ? `${parts.join(', ')} still exist only on this device.` : 'Some changes still exist only on this device.'
}

export function DataStorageSettings({
  continuity,
  storagePersistent,
  recoveryExportBusy,
  recoveryImportBusy,
  recoveryImportEnabled,
  recoveryInputRef,
  onExport,
  onImportRecovery,
  googleKeepController,
  googleKeepEnabled,
  googleKeepOpen,
  onGoogleKeepOpenChange,
  noteExportController,
  notesExport,
  onDeleteDeviceData,
}: DataStorageSettingsProps) {
  const needsBackup = Boolean(continuity?.hasUnsynchronizedWork)
  const [format, setFormat] = useState<'markdown' | 'json'>('markdown')
  const importsRef = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    if (googleKeepOpen && importsRef.current) importsRef.current.open = true
  }, [googleKeepOpen])
  return <div className="settings-data-overview">
    <section className="settings-data-health" data-tone={needsBackup ? 'warning' : continuity ? 'safe' : 'checking'} aria-live="polite">
      <span className="settings-data-health__icon" aria-hidden="true"><Icon name={needsBackup ? 'upload-error' : continuity ? 'check' : 'cloud'} /></span>
      <div>
        <strong>{needsBackup ? 'Protect changes on this device' : continuity ? 'This device is up to date' : 'Checking this device'}</strong>
        <p>{needsBackup && continuity
          ? pendingSummary(continuity)
          : continuity
            ? `No unsynchronized local work detected${storagePersistent === true ? '; offline storage is protected by the browser' : ''}.`
            : 'Reviewing local notes, images, and queued changes.'}</p>
      </div>
      {needsBackup ? <button className="btn" type="button" disabled={recoveryExportBusy} onClick={() => onExport('recovery')}>
        {recoveryExportBusy ? 'Preparing backup…' : 'Download recovery copy'}
      </button> : null}
    </section>

    <details id="settings-data-exports" className="collapse collapse-arrow settings-data-disclosure settings-data-task">
      <summary className="collapse-title">
        <span><strong>Export notes</strong><small>Download your notes or find a prepared export.</small></span>
        <NoteExportSettingsSummary controller={noteExportController} />
      </summary>
      <div className="collapse-content">
        <div className="settings-data-export-form">
          <div className="settings-data-row__copy"><strong className="settings-data-row__title">All notes</strong><span>A copy of your note text. For selected notes and image attachments, use Export in Browse.</span></div>
          <label className="settings-data-format">Format
            <select className="select" value={format} onChange={(event) => setFormat(event.target.value as 'markdown' | 'json')}>
              <option value="markdown">Markdown · readable text</option>
              <option value="json">JSON · structured data</option>
            </select>
          </label>
          <button className="btn btn-primary" type="button" disabled={notesExport.phase === 'working'} onClick={() => onExport(format)}>{notesExport.phase === 'working' ? 'Preparing…' : 'Download notes'}</button>
        </div>
        {notesExport.phase !== 'idle' ? <section className="settings-data-health settings-data-export-status" data-tone={notesExport.phase === 'working' ? 'checking' : notesExport.phase === 'done' ? 'safe' : 'warning'} role={notesExport.phase === 'failed' ? 'alert' : 'status'} aria-live="polite">
          <span className="settings-data-health__icon" aria-hidden="true"><Icon name={notesExport.phase === 'failed' ? 'upload-error' : notesExport.phase === 'done' ? 'check' : 'cloud'} /></span>
          <div><strong>{notesExport.title}</strong><p>{notesExport.detail}</p></div>
        </section> : null}
        {noteExportController.jobs.length > 0 || noteExportController.error || noteExportController.attentionJobId ? <NoteExportsPanel controller={noteExportController} /> : null}
        {noteExportController.attentionJobId ? <button className="btn btn-ghost settings-data-clear-status" type="button" onClick={noteExportController.dismissAttention}>Clear export status</button> : null}
      </div>
    </details>

    <details ref={importsRef} className="collapse collapse-arrow settings-data-disclosure settings-data-task">
      <summary className="collapse-title"><span><strong>Import notes</strong><small>Bring notes from another app or restore a recovery copy.</small></span>
        {googleKeepController.jobs.some((job) => job.state === 'awaiting_confirmation')
          ? <span className="settings-data-task__status">Review import</span>
          : googleKeepController.jobs.some((job) => ['uploading', 'scanning', 'importing', 'canceling', 'rolling_back'].includes(job.state))
            ? <span className="settings-data-task__status">Import in progress</span> : null}
      </summary>
      <div className="collapse-content">
        <div className="settings-data-row">
          <span className="settings-data-row__icon" aria-hidden="true"><Icon name="archive" /></span>
          <span className="settings-data-row__copy"><span className="settings-data-row__title">miniMany recovery copy</span><span>Restore a recovery JSON created by miniMany.</span></span>
          <button className="btn" type="button" onClick={() => recoveryInputRef.current?.click()} disabled={recoveryImportBusy || !recoveryImportEnabled}>
            {recoveryImportBusy ? 'Restoring…' : 'Choose recovery file'}
          </button>
          <input ref={recoveryInputRef} hidden type="file" accept="application/json,.json" onChange={onImportRecovery} tabIndex={-1} />
        </div>
        {!recoveryImportEnabled ? <p className="settings-data-inline-warning">Sign in and wait for account restore before using a recovery copy.</p> : null}
        <GoogleKeepImportPanel controller={googleKeepController} enabled={googleKeepEnabled} open={googleKeepOpen} onOpenChange={onGoogleKeepOpenChange} />
      </div>
    </details>

    {onDeleteDeviceData ? <div className="settings-account-danger" id="settings-data-delete-device">
      <span className="settings-row__label">
        <strong>Delete data on this device</strong>
        <span>You are not signed in, so your notes, images and Contexts exist only here. This erases all of them, right away. It cannot be undone.</span>
      </span>
      <button className="btn btn-error btn-outline" type="button" onClick={onDeleteDeviceData}>Delete data on this device…</button>
    </div> : null}
  </div>
}
